import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { nivelUsuario } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import { db } from "../../config/firebase";

const COLLECTION_RECEITAS = "financeiro_receitas";
const STATUS_OPTIONS = ["PENDENTE", "RECEBIDO", "VENCIDO", "CANCELADO"];

function toNumber(value) {
  const n = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

export default function FinanceiroReceitasFormPage() {
  const { tema } = useTheme();
  const { usuario } = useAuth();
  const { empresaAtual, modoGlobal } = useEmpresa();
  const navigate = useNavigate();
  const { id } = useParams();

  const [clientes, setClientes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const [form, setForm] = useState({
    empresaId: "",
    descricao: "",
    clienteOrigem: "",
    categoria: "",
    centroCusto: "",
    competencia: "",
    vencimento: "",
    dataRecebimento: "",
    valorPrevisto: "",
    desconto: "",
    valorRecebido: "",
    formaPagamento: "",
    status: "PENDENTE",
    observacoes: "",
  });

  const isEdicao = !!id;

  const restringirEmpresa = useMemo(() => {
    const nivel = nivelUsuario(usuario);
    return !modoGlobal || nivel < NIVEIS.TECNICO_SENIOR;
  }, [modoGlobal, usuario]);

  const empresaEscopoId = empresaAtual?.id || usuario?.empresaId || "";

  // Carregar empresas
  useEffect(() => {
    const carregarClientes = async () => {
      if (!usuario?.grupoId) return;
      try {
        const snap = await getDocs(
          query(collection(db, "empresas"), where("grupoId", "==", usuario.grupoId))
        );
        setClientes(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"))
        );
      } catch (e) {
        console.error("Erro ao carregar empresas:", e);
      }
    };

    carregarClientes();
  }, [usuario?.grupoId]);

  // Atualizar empresaId do form se restringir
  useEffect(() => {
    if (restringirEmpresa) {
      setForm((prev) => ({ ...prev, empresaId: empresaEscopoId || "" }));
    }
  }, [restringirEmpresa, empresaEscopoId]);

  // Carregar dados se edição
  useEffect(() => {
    if (!isEdicao) return;

    const carregar = async () => {
      setCarregando(true);
      setErro("");
      try {
        const docSnap = await getDoc(doc(db, COLLECTION_RECEITAS, id));
        if (docSnap.exists()) {
          const data = docSnap.data();
          setForm({
            empresaId: data.empresaId || "",
            descricao: data.descricao || "",
            clienteOrigem: data.clienteOrigem || "",
            categoria: data.categoria || "",
            centroCusto: data.centroCusto || "",
            competencia: data.competencia || "",
            vencimento: data.vencimento || "",
            dataRecebimento: data.dataRecebimento || "",
            valorPrevisto: String(data.valorPrevisto ?? ""),
            desconto: String(data.desconto ?? ""),
            valorRecebido: String(data.valorRecebido ?? ""),
            formaPagamento: data.formaPagamento || "",
            status: data.status || "PENDENTE",
            observacoes: data.observacoes || "",
          });
          setSucesso("");
        } else {
          setErro("Receita não encontrada.");
        }
      } catch (e) {
        console.error("Erro ao carregar receita:", e);
        setErro("Erro ao carregar receita.");
      } finally {
        setCarregando(false);
      }
    };

    carregar();
  }, [id, isEdicao]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (!usuario?.grupoId) {
      setErro("Usuário sem grupoId. Faça login novamente.");
      return;
    }

    const empresaId = restringirEmpresa ? empresaEscopoId : form.empresaId;
    if (!empresaId) {
      setErro("Selecione a empresa.");
      return;
    }

    if (!form.descricao.trim()) {
      setErro("Informe a descrição da receita.");
      return;
    }

    const valorPrevisto = toNumber(form.valorPrevisto);
    const desconto = toNumber(form.desconto);
    const valorRecebido = toNumber(form.valorRecebido);

    const cliente = clientes.find((c) => String(c.id) === String(empresaId));

    const payload = {
      grupoId: usuario.grupoId,
      empresaId,
      clienteNome: cliente?.nome || cliente?.razaoSocial || "",
      descricao: form.descricao.trim(),
      clienteOrigem: form.clienteOrigem.trim(),
      categoria: form.categoria.trim(),
      centroCusto: form.centroCusto.trim(),
      competencia: form.competencia || "",
      vencimento: form.vencimento || "",
      dataRecebimento: form.dataRecebimento || "",
      valorPrevisto,
      desconto,
      valorRecebido,
      formaPagamento: form.formaPagamento.trim(),
      status: form.status || "PENDENTE",
      observacoes: form.observacoes.trim(),
      updatedAt: serverTimestamp(),
    };

    setSalvando(true);
    try {
      if (isEdicao) {
        await updateDoc(doc(db, COLLECTION_RECEITAS, id), payload);
        setSucesso("Receita atualizada com sucesso.");
      } else {
        await addDoc(collection(db, COLLECTION_RECEITAS), {
          ...payload,
          createdAt: serverTimestamp(),
          criadoPor: usuario?.uid || usuario?.id || "",
        });
        setSucesso("Receita criada com sucesso.");
      }
      setTimeout(() => {
        navigate("/financeiro/receitas");
      }, 500);
    } catch (e2) {
      console.error("Erro ao salvar receita:", e2);
      setErro("Erro ao salvar receita.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <LayoutDesktop tema={tema} titulo={isEdicao ? "Editar receita" : "Nova receita"} subtitulo="Financeiro / Receitas">
      <section className="pc-card">
        {erro && <div className="pc-alert pc-alert-erro">{erro}</div>}
        {sucesso && <div className="pc-alert pc-alert-sucesso">{sucesso}</div>}

        {carregando ? (
          <div style={{ padding: 32, textAlign: "center" }}>Carregando...</div>
        ) : (
          <form onSubmit={handleSalvar} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, padding: "16px" }}>
            {/* Empresa */}
            {!restringirEmpresa && (
              <label className="pc-form-group">
                Empresa
                <select
                  value={form.empresaId}
                  onChange={(e) => setField("empresaId", e.target.value)}
                  className="pc-input"
                  disabled={salvando}
                >
                  <option value="">Selecione a empresa</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome || c.razaoSocial || c.id}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {/* Descrição */}
            <label className="pc-form-group pc-form-group-full">
              Descrição
              <input
                type="text"
                value={form.descricao}
                onChange={(e) => setField("descricao", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="Ex: Mensalidade janeiro, Consulta adicional"
              />
            </label>

            {/* Cliente de Origem */}
            <label className="pc-form-group">
              Cliente de origem
              <input
                type="text"
                value={form.clienteOrigem}
                onChange={(e) => setField("clienteOrigem", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="Ex: João Silva, Academia XYZ"
              />
            </label>

            {/* Categoria */}
            <label className="pc-form-group">
              Categoria
              <input
                type="text"
                value={form.categoria}
                onChange={(e) => setField("categoria", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="Ex: Mensalidade, Venda, Serviço"
              />
            </label>

            {/* Centro de Custo */}
            <label className="pc-form-group">
              Centro de custo
              <input
                type="text"
                value={form.centroCusto}
                onChange={(e) => setField("centroCusto", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="Ex: CC001, CC002"
              />
            </label>

            {/* Competência */}
            <label className="pc-form-group">
              Competência (mês/ano)
              <input
                type="text"
                value={form.competencia}
                onChange={(e) => setField("competencia", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="Ex: 01/2024"
              />
            </label>

            {/* Vencimento */}
            <label className="pc-form-group">
              Vencimento
              <input
                type="date"
                value={form.vencimento}
                onChange={(e) => setField("vencimento", e.target.value)}
                className="pc-input"
                disabled={salvando}
              />
            </label>

            {/* Data de Recebimento */}
            <label className="pc-form-group">
              Data de recebimento
              <input
                type="date"
                value={form.dataRecebimento}
                onChange={(e) => setField("dataRecebimento", e.target.value)}
                className="pc-input"
                disabled={salvando}
              />
            </label>

            {/* Valor Previsto */}
            <label className="pc-form-group">
              Valor previsto (R$)
              <input
                type="text"
                value={form.valorPrevisto}
                onChange={(e) => setField("valorPrevisto", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="0,00"
              />
            </label>

            {/* Desconto */}
            <label className="pc-form-group">
              Desconto (R$)
              <input
                type="text"
                value={form.desconto}
                onChange={(e) => setField("desconto", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="0,00"
              />
            </label>

            {/* Valor Recebido */}
            <label className="pc-form-group">
              Valor recebido (R$)
              <input
                type="text"
                value={form.valorRecebido}
                onChange={(e) => setField("valorRecebido", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="0,00"
              />
            </label>

            {/* Forma de Pagamento */}
            <label className="pc-form-group">
              Forma de pagamento
              <input
                type="text"
                value={form.formaPagamento}
                onChange={(e) => setField("formaPagamento", e.target.value)}
                className="pc-input"
                disabled={salvando}
                placeholder="Ex: Dinheiro, Cartão, Transferência"
              />
            </label>

            {/* Status */}
            <label className="pc-form-group">
              Status
              <select
                value={form.status}
                onChange={(e) => setField("status", e.target.value)}
                className="pc-input"
                disabled={salvando}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>

            {/* Observações */}
            <label className="pc-form-group pc-form-group-full">
              Observações
              <textarea
                value={form.observacoes}
                onChange={(e) => setField("observacoes", e.target.value)}
                className="pc-input"
                disabled={salvando}
                style={{ minHeight: "80px", fontFamily: "inherit" }}
                placeholder="Informações adicionais sobre a receita..."
              />
            </label>

            {/* Ações */}
            <div className="pc-form-actions" style={{ gridColumn: "1 / -1" }}>
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={() => navigate("/financeiro/receitas")}
                disabled={salvando}
              >
                Voltar
              </button>
              <button type="submit" className="pc-btn-primario" disabled={salvando || carregando}>
                {salvando ? "Salvando..." : isEdicao ? "Salvar alterações" : "Salvar receita"}
              </button>
            </div>
          </form>
        )}
      </section>
    </LayoutDesktop>
  );
}
